import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { fileUrl } from "@/lib/api";
import { Loader2, Image as ImageIcon } from "lucide-react";

/**
 * Visual preview of the FDP profiles available for the user's current
 * (sim_type, style, selected_categories) selection. Shows up to N
 * thumbnails per category so the user sees BEFORE generating exactly
 * which catalog entries the AI will draw inspiration from.
 *
 * Props:
 *  - simType: "INTERIOR" | "EXTERIOR" (required)
 *  - style: "ART_DECO" | "CLASSIC" | "MODERN" | "NEOCLASSIC" | null
 *  - categories: string[] (slugs)
 *  - limitPerCategory?: number (default 3)
 */
export default function CatalogPreview({
  simType,
  style,
  categories = [],
  limitPerCategory = 3,
}) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancel = false;
    if (!simType || !style) {
      // Defer to microtask — sync setState inside an effect upsets
      // react-hooks/set-state-in-effect.
      Promise.resolve().then(() => { if (!cancel) setData(null); });
      return () => { cancel = true; };
    }
    // Defer initial setLoading + fetch to a microtask too.
    Promise.resolve().then(() => {
      if (cancel) return;
      setLoading(true);
      const params = new URLSearchParams({
        sim_type: simType,
        style: style,
        limit_per_category: String(limitPerCategory),
      });
      if (categories && categories.length > 0) {
        params.set("categories", categories.join(","));
      }
      api
        .get(`/catalog/preview?${params.toString()}`)
        .then((r) => !cancel && setData(r.data))
        .catch(() => !cancel && setData(null))
        .finally(() => !cancel && setLoading(false));
    });
    return () => {
      cancel = true;
    };
  }, [simType, style, categories.join(","), limitPerCategory]);

  if (!simType || !style) return null;
  if (loading) {
    return (
      <div className="border border-white/5 rounded-md bg-zinc-900/30 px-4 py-3 flex items-center text-zinc-500 text-xs">
        <Loader2 size={12} className="animate-spin mr-2" />
        {t("preview.loading")}
      </div>
    );
  }
  if (!data || data.total_photos === 0) return null;

  const totalCodes = data.categories.reduce(
    (n, c) => n + (c.photos?.length || 0),
    0
  );

  // iter 83 (09.06.2026 production feedback): Sales/Partner reported
  // confusion — they saw 10 profiles in this strip and thought all 10 would
  // appear in the render, but the AI actually picks a subset. Show two
  // honest variants of the labels based on whether the user has actively
  // selected categories below:
  //   • selection empty → "Sample of the catalog (AI will pick a subset)"
  //   • selection ≥ 1   → "Profiles you picked — these go into the render"
  const hasUserSelection = (categories || []).length > 0;
  const headerKey = hasUserSelection ? "preview.header_selected" : "preview.header_sample";
  const footerKey = hasUserSelection ? "preview.footer_selected" : "preview.footer_sample";
  // Stronger border + tinted background when selection is binding, lighter
  // tone for the informational sample variant.
  const wrapperCls = hasUserSelection
    ? "border border-orange-500/40 bg-orange-500/10"
    : "border border-white/10 bg-zinc-900/40";

  return (
    <div
      className={`${wrapperCls} rounded-md px-4 py-3 space-y-3`}
      data-testid="catalog-preview-block"
      data-state={hasUserSelection ? "selected" : "sample"}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] uppercase tracking-[0.25em] text-orange-400 flex items-center gap-2">
          <ImageIcon size={12} />
          {t(headerKey)}
        </div>
        <div className="text-[10px] text-zinc-500">
          {t("preview.count", { profiles: totalCodes, categories: data.categories.length })}
        </div>
      </div>
      <div className="space-y-2">
        {data.categories.map((cat) =>
          cat.photos && cat.photos.length > 0 ? (
            <div
              key={cat.category_slug}
              className="flex items-center gap-3"
              data-testid={`catalog-preview-cat-${cat.category_slug}`}
            >
              <div className="text-[10px] text-zinc-400 w-28 shrink-0 uppercase tracking-wider truncate">
                {cat.category_name}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {cat.photos.map((p) => (
                  <div
                    key={p.id}
                    className="relative group"
                    title={`cp ${p.product_code}${p.style ? ` · ${p.style.replace("_", " ")}` : ""}`}
                  >
                    <img
                      src={fileUrl(p.url)}
                      alt={p.product_code}
                      className="w-14 h-14 object-cover rounded border border-white/10 bg-zinc-950"
                      loading="lazy"
                    />
                    <div className="absolute bottom-0 inset-x-0 bg-black/80 text-orange-300 text-[9px] text-center font-mono uppercase py-0.5 rounded-b">
                      {p.product_code}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null
        )}
      </div>
      <p className="text-[10px] text-zinc-500 leading-relaxed">
        {t(footerKey)}
      </p>
    </div>
  );
}
